import { createContext, useContext, useEffect, useMemo } from 'react'
import { useGetDetailedWorkspaceQuery } from '~/common/generated/apollo-operations'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { DetailedWorkspace } from '../renderers/workspaces/types'

interface WorkspaceDetailsContextValue {
  workspaceId: string
  workspace?: DetailedWorkspace | null
  loading: boolean
  errorMessage?: string
}

export function WorkspaceDetailsProvider({
  children,
  workspaceId,
}: {
  children: React.ReactNode
  workspaceId?: string
}) {
  const { dispatch } = useDossSignals()
  const { data, loading, error } = useGetDetailedWorkspaceQuery({
    variables: { workspaceId: workspaceId || '' },
    skip: !workspaceId,
  })

  const errorMessage = useMemo(() => {
    if (error) {
      return error.message
    }
    return undefined
  }, [error])

  // Send signal that workspace must be updated after new data loads.
  // Don't return new data here (separate useMemo below) to avoid infinite loop
  useEffect(() => {
    if (data?.workspace) {
      dispatch(DossSignalType.REFRESH_WORKSPACE)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps DossSignals dispatch always new ref, so infinite re-render
  }, [data])

  const workspace = useMemo(() => {
    return data?.workspace || null
  }, [data])

  return (
    <WorkspaceDetailsContext.Provider
      value={{
        workspaceId: workspaceId || '',
        workspace,
        loading,
        errorMessage,
      }}
    >
      {children}
    </WorkspaceDetailsContext.Provider>
  )
}

export const useWorkspaceDetails = () => {
  const context = useContext(WorkspaceDetailsContext)

  if (!context) {
    throw new Error('useWorkspaceDetails must be used within a WorkspaceDetailsProvider')
  }

  return context
}

const WorkspaceDetailsContext = createContext<WorkspaceDetailsContextValue | null>(null)
